import { useAuth } from '@/context/AuthContext';
import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = [
  {
    label: 'Prospect',
    color: '#94A3B8',
    bgLight: '#F1F5F9',
    textColor: '#475569',
    description: 'Initial contact created',
  },
  {
    label: 'Aware',
    color: '#3B82F6',
    bgLight: '#EFF6FF',
    textColor: '#1D4ED8',
    description: 'Replied or engaged once',
  },
  {
    label: 'Engaged',
    color: '#8B5CF6',
    bgLight: '#F5F3FF',
    textColor: '#6D28D9',
    description: 'Active in conversation',
  },
  {
    label: 'Qualified',
    color: '#00D95F',
    bgLight: '#ECFDF5',
    textColor: '#065F46',
    description: 'Flow submitted',
  },
  {
    label: 'Won',
    color: '#059669',
    bgLight: '#D1FAE5',
    textColor: '#064E3B',
    description: 'Converted to customer',
  },
];

function CountDisplay({ value, loading }) {
  if (loading) {
    return (
      <div className="flex items-end gap-1 mt-2">
        <div className="h-8 w-10 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }
  return (
    <p className="text-3xl font-bold text-slate-900 mt-2" data-testid="count-value">
      {value ?? 0}
    </p>
  );
}

export function DashboardOverview() {
  const { user, token } = useAuth();
  const firstName = user?.full_name?.split(' ')[0] || 'there';

  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/crm/lead-counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setCounts(data);
        setLastUpdated(new Date());
      }
    } catch {
      // silently fail — show 0s
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const stages = counts?.stages || {};
  const totalContacts = counts?.total_contacts ?? 0;
  const flowsActive = counts?.flows_active ?? 0;
  const thisMonth = counts?.this_month_qualified ?? 0;

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {/* Welcome */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Welcome back, {firstName}</h2>
          <p className="text-sm text-slate-500 mt-1">Here's your live lead pipeline overview</p>
        </div>
        <button
          onClick={fetchCounts}
          disabled={loading}
          data-testid="refresh-counts-btn"
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#00D1B2] transition-colors disabled:opacity-40 mt-1"
          title="Refresh counts"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Refresh'}
        </button>
      </div>

      {/* Pipeline Funnel */}
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">
          Lead Pipeline
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PIPELINE_STAGES.map(({ label, color, bgLight, textColor, description }) => (
            <div
              key={label}
              data-testid={`stage-card-${label.toLowerCase()}`}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow"
              style={{ borderTop: `3px solid ${color}` }}
            >
              <div className="mb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: bgLight, color: textColor }}
                >
                  {label}
                </span>
              </div>
              <CountDisplay value={stages[label]} loading={loading} />
              <p className="text-xs text-slate-400 mt-1.5">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Stats */}
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">
          Quick Stats
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
            data-testid="stat-card-total-contacts"
          >
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              Total Contacts
            </p>
            <CountDisplay value={totalContacts} loading={loading} />
            <p className="text-xs text-slate-400 mt-1">All Chatwoot contacts</p>
          </div>

          <div
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
            data-testid="stat-card-flows-active"
          >
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              Flows Active
            </p>
            <CountDisplay value={flowsActive} loading={loading} />
            <p className="text-xs text-slate-400 mt-1">Configured flows</p>
          </div>

          <div
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
            data-testid="stat-card-this-month"
          >
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              This Month
            </p>
            <CountDisplay value={thisMonth} loading={loading} />
            <p className="text-xs text-slate-400 mt-1">New flow submissions</p>
          </div>
        </div>
      </section>
    </div>
  );
}
